import { supabase, supabaseConfigured } from '@/lib/supabaseClient';
import { store } from '@/storage/sqliteStore';

const KEY = 'drilllog_active_organisation_id';
const LICENCE_KEY = 'drilllog_licence_cache';
const TENANT_SCOPED_STORES = ['Sheet','DrillRun','DepthAlert','Stocktake','DailyLog','DailyTask','File','AuditLog','SyncQueue','FeaturePermission','DocumentTemplate','AirCoreRun','AirCoreSample','RCRun','ShiftSnapshot'];

export async function ensureTenant({ organisationName } = {}) {
  if (!supabaseConfigured || !supabase) return null;
  const previous = localStorage.getItem(KEY);
  const { data, error } = await supabase.rpc('drilllog_ensure_tenant', { org_name: organisationName || null });
  if (error) throw error;
  const id = data?.organisation_id || null;
  if (id) {
    if (previous && previous !== id) await switchLocalTenant(previous, id);
    localStorage.setItem(KEY, id);
  }
  return data || null;
}

export async function getActiveTenantId() {
  const cached = localStorage.getItem(KEY) || null;
  if (!supabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase.rpc('drilllog_active_org_ids');
    if (error) throw error;
    const id = data?.[0] || null;
    const previous = cached;
    if (id && previous && previous !== id) await switchLocalTenant(previous, id);
    if (id) localStorage.setItem(KEY, id);
    return id || cached;
  } catch (error) {
    if (cached) {
      console.warn('[DrillLogPro] tenant RPC unavailable; using cached organisation:', error?.message || error);
      return cached;
    }
    throw error;
  }
}

async function switchLocalTenant(previous, next) {
  // A user who moves from Org A to Org B must not retain Org A's locally
  // cached operational records or queued mutations. Keep identity/auth state.
  if (!previous || previous === next) return;
  for (const name of TENANT_SCOPED_STORES) {
    try {
      const table = store(name);
      const records = await table.all();
      if (name === 'SyncQueue') {
        await table.bulkDelete(records.map((r) => r.id));
      } else {
        const ids = records.filter((r) => !r.organisation_id || r.organisation_id === previous).map((r) => r.id);
        if (ids.length) await table.bulkDelete(ids);
      }
    } catch (error) {
      console.warn(`[DrillLogPro] tenant cache cleanup skipped for ${name}:`, error?.message || error);
    }
  }
  localStorage.removeItem(LICENCE_KEY);
}

export function clearTenantContext() {
  try { localStorage.removeItem(KEY); localStorage.removeItem(LICENCE_KEY); } catch (_) { /* noop */ }
}

export async function getLicence() {
  const organisationId = await getActiveTenantId();
  if (!organisationId || !supabaseConfigured || !supabase) return null;
  const { data, error } = await supabase.from('drilllog_licences').select('*').eq('organisation_id', organisationId).maybeSingle();
  if (error) throw error;
  if (data) {
    try { localStorage.setItem(LICENCE_KEY, JSON.stringify(data)); } catch (_) { /* noop */ }
  }
  return data || null;
}

export function getCachedLicence() {
  try { return JSON.parse(localStorage.getItem(LICENCE_KEY) || 'null'); } catch (_) { return null; }
}

export function isLicenceActive(licence) {
  if (!licence) return false;
  if (licence.status === 'active') return !licence.expires_at || new Date(licence.expires_at).getTime() > Date.now();
  if (licence.status === 'trial') return Boolean(licence.trial_ends_at) && new Date(licence.trial_ends_at).getTime() > Date.now();
  return false;
}

export function trialDaysRemaining(licence) {
  if (!licence?.trial_ends_at) return null;
  return Math.max(0, Math.ceil((new Date(licence.trial_ends_at).getTime() - Date.now()) / 86400000));
}

export async function requireActiveLicence() {
  const licence = await getLicence();
  if (!isLicenceActive(licence)) {
    const error = new Error(licence?.status === 'trial' ? 'Your DrillLogPro 14-day trial has expired. Choose a fleet plan to continue.' : 'Your DrillLogPro licence is not active.');
    error.status = 402;
    error.code = 'DLP_LICENCE_INACTIVE';
    throw error;
  }
  return licence;
}
